import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { Sheet } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/input';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useMembers } from '@/features/workspace/api';
import { useLocations } from '@/features/locations/api';
import { useAddLeadActivity, useConvertLead, useLead, useUpdateLead } from './api';
import { useLocaleFormatters } from '@/lib/format';
import { formatLeadVehicle } from './vehicle';

export function LeadDrawer({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const { t } = useTranslation('leads');
  const { formatDateTime } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const { data: lead } = useLead(workspaceId, leadId);
  const { data: members } = useMembers(workspaceId);
  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const { data: locationsData } = useLocations(workspaceId);
  const activeLocations = (locationsData?.items ?? []).filter((l) => l.isActive);
  const showLocationSelect = gateOn && activeLocations.length >= 2;
  const updateLead = useUpdateLead(workspaceId);
  const addActivity = useAddLeadActivity(workspaceId, leadId);
  const [note, setNote] = useState('');
  const navigate = useNavigate();
  const convertLead = useConvertLead(workspaceId, leadId);

  async function handleConvert() {
    const result = await convertLead.mutateAsync({});
    toast.success(t('toast.converted'));
    onClose();
    navigate(`/quotes/new?quoteId=${result.quoteId}`);
  }

  if (!lead) return null;

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()} title={lead.name}>
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-1 text-sm">
          <div className="text-muted">{lead.phone || '-'} · {lead.email || '-'}</div>
          <div className="text-muted">{t(`source.${lead.source}`)}</div>
          {formatLeadVehicle(lead) ? <div className="text-ink">{formatLeadVehicle(lead)}</div> : null}
          {lead.requirement ? <p className="mt-1 whitespace-pre-line text-ink">{lead.requirement}</p> : null}
        </div>

        {lead.photos.length > 0 ? (
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-muted">{t('drawer.photos')}</div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {lead.photos.map((p, i) => (
                <a key={p.url} href={p.url} target="_blank" rel="noreferrer">
                  <img src={p.url} alt={t('drawer.photoAlt', { index: i + 1 })} className="aspect-square w-full object-cover" loading="lazy" />
                </a>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <Label>{t('table.status')}</Label>
          <select
            value={lead.status}
            onChange={(e) => {
              const status = e.target.value as typeof lead.status;
              if (status === 'lost') {
                const reason = prompt(t('prompts.lostReason'));
                if (!reason) return;
                updateLead.mutate({ leadId, input: { status, lostReason: reason } });
              } else {
                updateLead.mutate({ leadId, input: { status } });
              }
            }}
            className="h-8 w-40 rounded-[var(--radius-input)] border border-border bg-surface px-2 text-sm"
          >
            {(lead.status === 'qualified' ? ['qualified', 'new', 'contacted', 'quoted', 'won', 'lost'] : ['new', 'contacted', 'quoted', 'won', 'lost']).map((s) => (
              <option key={s} value={s}>
                {t(`status.lead.${s}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <Label>{t('form.assignedTo')}</Label>
          <select
            value={lead.assignedTo ?? ''}
            onChange={(e) => updateLead.mutate({ leadId, input: { assignedTo: e.target.value || null } })}
            className="h-8 w-40 rounded-[var(--radius-input)] border border-border bg-surface px-2 text-sm"
          >
            <option value="">{t('unassigned')}</option>
            {(members ?? []).map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.fullName}
              </option>
            ))}
          </select>
        </div>

        {showLocationSelect ? (
          <div className="flex items-center gap-2">
            <Label>{t('form.location')}</Label>
            <select
              value={lead.locationId ?? ''}
              onChange={(e) => updateLead.mutate({ leadId, input: { locationId: e.target.value } })}
              className="h-8 w-40 rounded-[var(--radius-input)] border border-border bg-surface px-2 text-sm"
            >
              {activeLocations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
        ) : null}

        {lead.status !== 'quoted' && lead.status !== 'won' ? (
          <Button loading={convertLead.isPending} onClick={handleConvert}>{t('drawer.convertToQuote')}</Button>
        ) : null}

        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-muted">{t('drawer.activity')}</div>
          <div className="mt-2 flex flex-col gap-2">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('drawer.notePlaceholder')}
              rows={2}
              className="rounded-[var(--radius-input)] border border-border bg-surface px-3 py-2 text-sm outline-none focus-visible:border-accent"
            />
            <Button
              size="sm"
              variant="secondary"
              disabled={!note.trim()}
              onClick={async () => {
                await addActivity.mutateAsync({ type: 'note', body: note });
                setNote('');
              }}
            >
              {t('drawer.addNote')}
            </Button>
          </div>
          <div className="mt-3 flex flex-col gap-3">
            {lead.activities.map((a) => (
              <div key={a.id} className="border-l-2 border-border pl-3 text-sm">
                <div className="text-ink">{a.body}</div>
                <div className="text-xs text-muted">{formatDateTime(new Date(a.createdAt))}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </Sheet>
  );
}
